import { useState } from "react";
import { api, fileUrl, formatApiError, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Tv, Sparkles, MapPin, Heart, CheckCircle2, Video, X } from "lucide-react";
import { PROVINCES } from "@/lib/constants";

const heroImg = "https://images.unsplash.com/photo-1638848424383-c59f174814ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxjYW5hZGlhbiUyMGxhbmRzY2FwZSUyMG1vdW50YWlucyUyMGxha2UlMjBiYW5mZnxlbnwwfHx8fDE3ODQ2NjYxODN8MA&ixlib=rb-4.1.0&q=85";

export default function Apply() {
  const [form, setForm] = useState({
    full_name: "", age: 25, email: "", phone: "",
    city: "", province: "Ontario", single_status: false,
    availability: "", why_pick_me: "", ideal_partner: "", social_handle: "",
    video_path: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoName, setVideoName] = useState("");
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadVideo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please upload a video file (mp4, mov, webm).");
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video is too large. Max 100MB.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploadingVideo(true);
    try {
      const { data } = await api.post("/applications/upload-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("video_path", data.path);
      setVideoName(file.name);
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploadingVideo(false); }
  };

  const removeVideo = () => {
    set("video_path", "");
    setVideoName("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.single_status) { toast.error("You must confirm you're single to apply."); return; }
    setSubmitting(true);
    try {
      await api.post("/applications", { ...form, age: Number(form.age) });
      setDone(true);
      toast.success("Application submitted!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-6" />
        <h1 className="font-serif text-5xl">Application received.</h1>
        <p className="mt-4 text-muted-foreground text-lg">
          Our casting team reviews every single submission. If your story feels right, we'll reach out within 2 weeks.
        </p>
        <p className="mt-2 text-muted-foreground">Meanwhile — polish up your profile so we know exactly who to call.</p>
        <a href="/profile" data-testid="apply-success-profile" className="inline-block mt-8 text-primary link-underline font-medium">Edit your profile →</a>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-accent/70" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-32 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium mb-6 backdrop-blur">
            <Tv className="w-3 h-3" /> Casting now for Season 1 in the GTHA
          </div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] max-w-3xl">Apply to be on <span className="italic">Finding Love in Canada</span></h1>
          <p className="mt-6 text-white/90 text-lg max-w-2xl leading-relaxed">
            A new dating series filmed across the Greater Toronto & Hamilton Area. If you're single, ready, and have a story worth telling — we'd love to hear from you.
          </p>
          <div className="mt-8 flex flex-wrap gap-6 text-sm text-white/85">
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Filmed across the GTHA</div>
            <div className="flex items-center gap-2"><Heart className="w-4 h-4" /> Real dates, no scripts</div>
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Open to GTHA residents 18+</div>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        <form onSubmit={submit} className="bg-card border border-border rounded-3xl p-8 md:p-10 space-y-6">
          <div>
            <h2 className="font-serif text-3xl">Tell us about you</h2>
            <p className="text-muted-foreground text-sm mt-1">Take your time. Real stories stand out.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Full name</Label>
              <Input data-testid="apply-name" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label>Age</Label>
              <Input data-testid="apply-age" required type="number" min={18} max={99} value={form.age} onChange={(e) => set("age", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Email</Label>
              <Input data-testid="apply-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="apply-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="+1 (555) 123-4567" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>City</Label>
              <Input data-testid="apply-city" required value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label>Province / Territory</Label>
              <Select value={form.province} onValueChange={(v) => set("province", v)}>
                <SelectTrigger data-testid="apply-province" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>When are you available to film? (approx. dates / months)</Label>
            <Input data-testid="apply-availability" required value={form.availability} onChange={(e) => set("availability", e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="June–August, weekends" />
          </div>

          <div>
            <Label>Why should we pick you?</Label>
            <Textarea data-testid="apply-why" required value={form.why_pick_me} onChange={(e) => set("why_pick_me", e.target.value)} className="mt-1.5 min-h-28 rounded-xl" placeholder="What makes your story worth telling?" />
          </div>

          <div>
            <Label>Your ideal partner</Label>
            <Textarea data-testid="apply-ideal" required value={form.ideal_partner} onChange={(e) => set("ideal_partner", e.target.value)} className="mt-1.5 min-h-28 rounded-xl" placeholder="Not a list of demands — a vibe. Values, energy, humour…" />
          </div>

          <div>
            <Label>Contact handle (optional)</Label>
            <Input data-testid="apply-social" value={form.social_handle} onChange={(e) => set("social_handle", e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="@youremail" />
          </div>

          <div>
            <Label>Video pitch (optional)</Label>
            <p className="text-xs text-muted-foreground mt-1">Show us who you are. A 30–90 second video works best. Max 100MB · MP4, MOV, or WebM.</p>
            {form.video_path ? (
              <div className="mt-3 rounded-xl border border-border p-4 bg-secondary/40">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Video className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{videoName || "Uploaded video"}</div>
                    <div className="text-xs text-muted-foreground">Ready to submit</div>
                  </div>
                  <button
                    type="button"
                    data-testid="apply-video-remove"
                    onClick={removeVideo}
                    className="w-8 h-8 rounded-full hover:bg-background flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
                    aria-label="Remove video"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <video
                  controls
                  className="mt-3 w-full max-h-72 rounded-lg bg-black"
                  src={fileUrl(form.video_path)}
                  data-testid="apply-video-preview"
                />
              </div>
            ) : (
              <label
                data-testid="apply-video-label"
                className="mt-3 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors rounded-xl px-6 py-10 cursor-pointer text-muted-foreground"
              >
                {uploadingVideo ? (
                  <div className="text-sm animate-pulse">Uploading video… this may take a moment</div>
                ) : (
                  <>
                    <Video className="w-6 h-6 text-primary" />
                    <div className="text-sm text-foreground font-medium">Upload a video pitch</div>
                    <div className="text-xs">Drag a file or click to browse</div>
                  </>
                )}
                <input
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,video/x-m4v,video/*"
                  className="hidden"
                  data-testid="apply-video-input"
                  onChange={(e) => e.target.files?.[0] && uploadVideo(e.target.files[0])}
                  disabled={uploadingVideo}
                />
              </label>
            )}
          </div>

          <label className="flex items-start gap-3 pt-2">
            <Checkbox
              data-testid="apply-single-check"
              checked={form.single_status}
              onCheckedChange={(v) => set("single_status", !!v)}
              className="mt-1"
            />
            <span className="text-sm">I confirm I'm single, 18+, and legally allowed to appear on television in Canada.</span>
          </label>

          <div className="pt-4 border-t border-border">
            <Button
              data-testid="apply-submit-btn" type="submit" disabled={submitting || uploadingVideo}
              className="w-full h-12 rounded-full bg-primary hover:bg-primary/90"
            >
              <Tv className="w-4 h-4 mr-1.5" />
              {submitting ? "Sending…" : uploadingVideo ? "Waiting for video…" : "Submit application"}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
